import { Component, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { Router } from "@angular/router"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatSelectModule } from "@angular/material/select"
import { PageHeaderComponent } from "../../../shared/page-header/page-header.component"
import { DataLoaderComponent } from "../../../shared/data-loader/data-loader.component"
import { AuthService } from "../../../services/auth.service"
import { SupportAnalytics, SupportService } from "../../../services/support.service"

@Component({
  selector: "app-support-analytics",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    PageHeaderComponent,
    DataLoaderComponent,
  ],
  templateUrl: "./support-analytics.component.html",
  styleUrl: "./support-analytics.component.css",
})
export class SupportAnalyticsComponent implements OnInit {
  analytics: SupportAnalytics | null = null
  loading = false
  selectedPeriod = "month"

  periods = [
    { value: "week", label: "Last 7 Days" },
    { value: "month", label: "Last 30 Days" },
    { value: "quarter", label: "Last 90 Days" },
    { value: "year", label: "Last 12 Months" },
  ]

  constructor(
    private supportService: SupportService,
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.authService.getCurrentUserInfo()?.role?.toLowerCase() !== "admin") {
      this.router.navigate(["/help/tickets"])
      return
    }
    this.loadAnalytics()
  }

  loadAnalytics(): void {
    this.loading = true
    this.supportService.getAnalytics(this.selectedPeriod).subscribe({
      next: (res) => {
        this.analytics = this.normalizeAnalytics(res)
        this.loading = false
      },
      error: () => {
        this.analytics = null
        this.loading = false
      },
    })
  }

  onPeriodChange(): void {
    this.loadAnalytics()
  }

  getResolutionColor(): string {
    const rate = this.analytics?.overview?.resolution_rate || 0
    if (rate >= 80) return "#86c440"
    if (rate >= 50) return "#f97316"
    return "#dc2626"
  }

  getCategoryLabel(value: string): string {
    const labels: Record<string, string> = {
      platform_technical: "Platform/Technical",
      payment_billing: "Payment & Billing",
      account_settings: "Account & Settings",
      station_rates_pricing: "Station Rates",
      order_scheduling: "Order & Scheduling",
      commercial_production: "Production",
      other: "Other",
    }
    return labels[value] || value
  }

  get periodLabel(): string {
    return this.periods.find((p) => p.value === this.selectedPeriod)?.label ?? ""
  }

  get chartMax(): number {
    const series = this.analytics?.over_time ?? []
    return Math.max(0, ...series.map((d) => Math.max(d.created, d.resolved)))
  }

  /** Bar height as a percentage of the tallest value in the series. */
  barPct(count: number): number {
    if (this.chartMax === 0) return 0
    if (count === 0) return 0
    return Math.max(3, (count / this.chartMax) * 100)
  }

  categoryWidth(count: number): number {
    const total = this.analytics?.overview?.total_tickets || 0
    if (!total) return 0
    return (count / total) * 100
  }

  routingPct(count: number): number {
    const total = this.analytics?.overview?.total_tickets || 0
    if (!total) return 0
    return (count / total) * 100
  }

  stationResolution(ticketCount: number, openCount: number): number {
    if (!ticketCount) return 0
    return ((ticketCount - openCount) / ticketCount) * 100
  }

  private normalizeAnalytics(res: any): SupportAnalytics {
    const source = res?.overview ? res : res?.analytics ?? res
    return {
      period: source?.period || this.selectedPeriod,
      overview: {
        total_tickets: Number(source?.overview?.total_tickets ?? 0),
        open_count: Number(source?.overview?.open_count ?? 0),
        in_progress_count: Number(source?.overview?.in_progress_count ?? 0),
        resolved_count: Number(source?.overview?.resolved_count ?? 0),
        closed_count: Number(source?.overview?.closed_count ?? 0),
        high_priority_count: Number(source?.overview?.high_priority_count ?? 0),
        avg_resolution_hours: Number(source?.overview?.avg_resolution_hours ?? 0),
        resolution_rate: Number(source?.overview?.resolution_rate ?? 0),
      },
      by_category: Array.isArray(source?.by_category) ? source.by_category : [],
      over_time: Array.isArray(source?.over_time) ? source.over_time : [],
      by_routing: Array.isArray(source?.by_routing) ? source.by_routing : [],
      top_stations: Array.isArray(source?.top_stations) ? source.top_stations : [],
    }
  }
}
